/**
 * ScheduleList Component
 *
 * Unified schedule list with filter tabs, card grid, and actions.
 * Replaces the old workflow-schedules/ScheduleList + ScheduleCard combo
 * with a simpler design backed by the Temporal unified schedule API.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Calendar,
  Clock,
  Plus,
  Search,
  RefreshCw,
  Workflow,
  Settings,
  Play,
  Edit,
  Trash2,
  Power,
  MoreVertical,
  ExternalLink,
} from 'lucide-react';
import {
  useScheduleList,
  useToggleSchedule,
  useTriggerSchedule,
  useDeleteSchedule,
} from '../hooks/useSchedules';
import type {
  UnifiedScheduleResponse,
  UnifiedScheduleType,
} from '@/types/schedule';
import { UNIFIED_SCHEDULE_TYPE_LABELS } from '@/types/schedule';

type TypeFilter = UnifiedScheduleType | 'all';

interface ScheduleListProps {
  onEdit?: (schedule: UnifiedScheduleResponse) => void;
  onCreate?: () => void;
}

/** Simple date formatting: MM-DD HH:mm */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}-${day} ${hours}:${minutes}`;
}

export function ScheduleList({ onEdit, onCreate }: ScheduleListProps) {
  // Filter state
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Delete / trigger confirmation
  const [deleteTarget, setDeleteTarget] = useState<UnifiedScheduleResponse | null>(null);
  const [triggerTarget, setTriggerTarget] = useState<UnifiedScheduleResponse | null>(null);

  // Card menu
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Queries — pass schedule_type only when filtering
  const queryParams = typeFilter !== 'all' ? { schedule_type: typeFilter } : undefined;
  const { data, isLoading, error, refetch, isFetching } = useScheduleList(queryParams);

  // Mutations
  const toggleSchedule = useToggleSchedule();
  const triggerSchedule = useTriggerSchedule();
  const deleteSchedule = useDeleteSchedule();

  const schedules = data?.schedules ?? [];

  // Client-side search filter
  const filteredSchedules = searchQuery
    ? schedules.filter((s) => {
        const q = searchQuery.toLowerCase();
        return (
          s.name.toLowerCase().includes(q) ||
          s.description?.toLowerCase().includes(q) ||
          s.cron_expression.toLowerCase().includes(q)
        );
      })
    : schedules;

  // Counts per type (from unfiltered list when on "all", otherwise from the filtered query)
  const countAll = schedules.length;

  // Handlers
  const handleToggle = async (id: string) => {
    try {
      await toggleSchedule.mutateAsync(id);
    } catch {
      // Error handled by hook
    }
  };

  const handleTriggerConfirm = async () => {
    if (!triggerTarget) return;
    try {
      await triggerSchedule.mutateAsync(triggerTarget.id);
      setTriggerTarget(null);
    } catch {
      // Error handled by hook
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSchedule.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // Error handled by hook
    }
  };

  const isActionLoading =
    toggleSchedule.isPending || triggerSchedule.isPending || deleteSchedule.isPending;

  // --- Render helpers ---

  const getTypeBadge = (type: UnifiedScheduleType) => {
    if (type === 'workflow') {
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 inline-flex items-center gap-1">
          <Workflow className="h-3 w-3" />
          {UNIFIED_SCHEDULE_TYPE_LABELS.workflow}
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 inline-flex items-center gap-1">
        <Settings className="h-3 w-3" />
        {UNIFIED_SCHEDULE_TYPE_LABELS.system_task}
      </span>
    );
  };

  const getEnabledBadge = (enabled: boolean) => {
    if (enabled) {
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
          已启用
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
        已禁用
      </span>
    );
  };

  const getSubtitle = (schedule: UnifiedScheduleResponse) => {
    if (schedule.schedule_type === 'workflow' && schedule.workflow_config) {
      return `工作流: ${schedule.workflow_config.workflow_id}`;
    }
    if (schedule.schedule_type === 'system_task' && schedule.system_task_config) {
      return `任务类型: ${schedule.system_task_config.task_type}`;
    }
    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
          <p className="text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-red-500">
          <p>加载失败</p>
          <p className="text-sm">{(error as Error).message}</p>
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
            重试
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            定时任务
          </h2>
          <p className="text-sm text-gray-500">{countAll} 个定时任务</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            刷新
          </Button>
          {onCreate && (
            <Button variant="primary" onClick={onCreate}>
              <Plus className="h-4 w-4 mr-1" />
              创建任务
            </Button>
          )}
        </div>
      </div>

      {/* Type filter tabs */}
      <Tabs value={typeFilter} onValueChange={(v) => setTypeFilter(v as TypeFilter)}>
        <TabsList>
          <TabsTrigger value="all" className="flex items-center gap-2">
            全部
          </TabsTrigger>
          <TabsTrigger value="system_task" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            系统任务
          </TabsTrigger>
          <TabsTrigger value="workflow" className="flex items-center gap-2">
            <Workflow className="h-4 w-4" />
            工作流
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="搜索任务名称..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Empty state */}
      {filteredSchedules.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium mb-1">
              {searchQuery ? '没有匹配的任务' : '暂无定时任务'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {searchQuery ? '尝试调整搜索条件' : '创建您的第一个定时任务开始使用'}
            </p>
            {onCreate && !searchQuery && (
              <Button variant="secondary" onClick={onCreate}>
                创建任务
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Schedule card grid */}
      {filteredSchedules.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredSchedules.map((schedule) => {
            const subtitle = getSubtitle(schedule);
            return (
              <Card
                key={schedule.id}
                className={!schedule.is_enabled ? 'opacity-60' : ''}
              >
                <CardContent className="p-4">
                  {/* Card header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="space-y-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getTypeBadge(schedule.schedule_type)}
                        {getEnabledBadge(schedule.is_enabled)}
                      </div>
                      <h3 className="text-base font-medium flex items-center gap-2 truncate">
                        <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        {schedule.name}
                      </h3>
                      {subtitle && (
                        <p className="text-sm text-gray-500 truncate">{subtitle}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleToggle(schedule.id)}
                      disabled={isActionLoading}
                      className={`p-1 rounded ml-2 ${
                        schedule.is_enabled
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={schedule.is_enabled ? '禁用' : '启用'}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Cron expression */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="font-mono text-xs">{schedule.cron_expression}</span>
                    <span className="text-gray-400">({schedule.timezone})</span>
                  </div>

                  {/* Description */}
                  {schedule.description && (
                    <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                      {schedule.description}
                    </p>
                  )}

                  {/* Temporal ID */}
                  <div className="text-xs text-gray-400 mb-3 truncate">
                    Temporal: {schedule.temporal_schedule_id}
                  </div>

                  {/* Created at */}
                  <div className="text-xs text-gray-400 mb-3">
                    创建于 {formatDate(schedule.created_at)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setTriggerTarget(schedule)}
                      disabled={isActionLoading || !schedule.is_enabled}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      立即执行
                    </Button>

                    <div className="flex items-center gap-1">
                      {/* Temporal Web UI link */}
                      <a
                        href={`/temporal/namespaces/default/schedules/${schedule.temporal_schedule_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded hover:bg-gray-100 text-gray-500"
                        title="在 Temporal 中查看"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>

                      {/* More actions menu */}
                      <div className="relative">
                        <button
                          onClick={() =>
                            setMenuOpenId(menuOpenId === schedule.id ? null : schedule.id)
                          }
                          className="p-2 rounded hover:bg-gray-100"
                        >
                          <MoreVertical className="h-4 w-4 text-gray-500" />
                        </button>

                        {menuOpenId === schedule.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setMenuOpenId(null)}
                            />
                            <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                              <button
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                onClick={() => {
                                  setMenuOpenId(null);
                                  onEdit?.(schedule);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                                编辑
                              </button>
                              <button
                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                onClick={() => {
                                  setMenuOpenId(null);
                                  setDeleteTarget(schedule);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                                删除
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Trigger confirmation modal */}
      {triggerTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setTriggerTarget(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">确认立即执行</h3>
            <p className="text-gray-600 mb-4">
              确定要立即执行任务 &quot;{triggerTarget.name}&quot; 吗？
            </p>
            <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded mb-4">
              此操作将立即触发一次执行，请确保这是您期望的操作。
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setTriggerTarget(null)}>
                取消
              </Button>
              <Button
                variant="primary"
                onClick={handleTriggerConfirm}
                disabled={triggerSchedule.isPending}
                loading={triggerSchedule.isPending}
              >
                <Play className="h-4 w-4 mr-1" />
                确认执行
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">确认删除</h3>
            <p className="text-gray-600 mb-4">
              确定要删除定时任务 &quot;{deleteTarget.name}&quot; 吗？此操作将同时删除 Temporal
              中的调度，无法撤销。
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
                取消
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteConfirm}
                disabled={deleteSchedule.isPending}
                loading={deleteSchedule.isPending}
              >
                删除
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
